import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Calendar as CalendarIcon, RefreshCw, Link as LinkIcon, Copy, Plus, Trash2, AlertTriangle, Loader2, ArrowRightLeft, History, Ban } from "lucide-react";
import { api, extractErr, usePortalAuth } from "@/portal/PortalAuth";
import { btn, ghost, card, inp } from "@/portal/PortalShell";
import Listings from "@/portal/Listings";
import { AirbnbConnect } from "@/portal/AirbnbConnect";
import { AddListingDrawer, AddSyncDrawer } from "@/portal/AddListing";

const BOOKING_STATUSES = ["Pending Review", "Availability Confirmed", "Awaiting Payment", "Confirmed", "Declined", "Cancelled"];
const PUBLIC_ICAL_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/booking/ical`;
const STALE_HOURS = 24;

export default function Bookings() {
  const [tab, setTab] = useState("Calendar");
  const [drawer, setDrawer] = useState("");
  const [openId, setOpenId] = useState(null);
  const { user } = usePortalAuth();
  const canManage = ["admin", "manager"].includes(user?.role);
  const tabs = ["Calendar", "Reservations", "Listings", "Airbnb Sync"];
  return (
    <div data-testid="bookings-panel" className="space-y-5">
      {canManage && (
        <div className="flex flex-wrap gap-3">
          <button onClick={() => setDrawer("listing")} data-testid="add-listing-btn" className={btn + " text-sm"}><Plus className="w-4 h-4" /> Add Listing</button>
          <button onClick={() => setDrawer("sync")} data-testid="add-sync-btn" className={btn + " text-sm"}><Plus className="w-4 h-4" /> Add Airbnb Sync</button>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`bookings-tab-${t.replace(/\s+/g, "-").toLowerCase()}`}
            className={`rounded-full px-4 py-1.5 text-sm border transition-colors ${tab === t ? "bg-[#1C1917] text-white border-[#1C1917]" : "bg-white border-[#E7E5E4] text-[#44403C]"}`}>{t}</button>
        ))}
      </div>
      {tab === "Calendar" && <CalendarView />}
      {tab === "Reservations" && <ReservationsList />}
      {tab === "Listings" && <Listings key={openId || "list"} initialEditId={openId} initialTab={openId ? "Photos" : "Basics"} />}
      {tab === "Airbnb Sync" && <AirbnbSync key={drawer} />}
      {drawer === "listing" && <AddListingDrawer onClose={() => setDrawer("")} onCreated={(id) => { setDrawer(""); setOpenId(id); setTab("Listings"); }} />}
      {drawer === "sync" && <AddSyncDrawer onClose={() => setDrawer("")} />}
    </div>
  );
}

// ---------- Calendar ----------
function CalendarView() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
  const [roomId, setRoomId] = useState("");
  const [rooms, setRooms] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  const { start, end, days } = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    const first = new Date(Date.UTC(y, m - 1, 1));
    const last = new Date(Date.UTC(y, m, 0));
    const days = [];
    for (let d = 1; d <= last.getUTCDate(); d++) days.push(new Date(Date.UTC(y, m - 1, d)).toISOString().slice(0, 10));
    return { start: first.toISOString().slice(0, 10), end: new Date(Date.UTC(y, m, 1)).toISOString().slice(0, 10), days };
  }, [month]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { start, end };
      if (roomId) params.room_id = roomId;
      const { data } = await api.get("/admin/booking/calendar", { params });
      setEvents(data.events);
      setRooms(data.rooms);
    } catch (e) { toast.error(extractErr(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [month, roomId]);

  const eventsByRoom = useMemo(() => {
    const map = {};
    for (const r of rooms) map[r.id] = [];
    for (const e of events) (map[e.room_id] = map[e.room_id] || []).push(e);
    return map;
  }, [rooms, events]);

  const covers = (ev, day) => day >= ev.start && day < ev.end;

  const dot = (ev) => {
    if (ev.source === "airbnb") return "bg-[#FF5A5F]"; // Airbnb coral
    if (ev.source === "manual") return "bg-[#78716C]"; // grey
    if (ev.status === "Pending Review") return "bg-[#D4A017]"; // amber
    return "bg-[#8A9A86]"; // sage for direct reservations
  };

  return (
    <div className="space-y-4" data-testid="booking-calendar">
      <div className={card}>
        <div className="flex flex-wrap items-end gap-3 justify-between">
          <div className="flex items-center gap-3">
            <CalendarIcon className="w-5 h-5 text-[#8A9A86]" strokeWidth={1.5} />
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="calendar-month" className={inp + " max-w-[160px]"} />
            <select value={roomId} onChange={(e) => setRoomId(e.target.value)} data-testid="calendar-room-filter" className={inp + " max-w-[240px]"}>
              <option value="">All rooms</option>
              {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-3 text-xs text-[#78716C]">
            <Legend color="#FF5A5F" label="Airbnb" />
            <Legend color="#8A9A86" label="Masaya confirmed" />
            <Legend color="#D4A017" label="Pending review" />
            <Legend color="#78716C" label="Manual block" />
            <button onClick={load} className={ghost + " text-xs"}>{loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Refresh</button>
          </div>
        </div>
      </div>

      {/* Month grid: rooms as rows, days as columns */}
      <div className="rounded-2xl border border-[#E7E5E4] bg-white overflow-x-auto" data-testid="calendar-grid">
        <table className="min-w-full text-xs">
          <thead>
            <tr className="bg-[#F5F5F4] border-b border-[#E7E5E4]">
              <th className="sticky left-0 bg-[#F5F5F4] text-left px-3 py-2 font-medium text-[#44403C] min-w-[200px]">Room</th>
              {days.map((d) => (
                <th key={d} className="px-1 py-2 font-normal text-[#78716C] min-w-[26px]">{Number(d.slice(-2))}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rooms.length === 0 && (
              <tr><td colSpan={days.length + 1} className="px-4 py-6 text-[#78716C] text-center">No rooms match.</td></tr>
            )}
            {rooms.map((r) => (
              <tr key={r.id} className="border-b border-[#F5F5F4] hover:bg-[#FAFAF9]">
                <td className="sticky left-0 bg-white px-3 py-2 text-[#1C1917] whitespace-nowrap" data-testid={`cal-row-${r.slug}`}>{r.name}</td>
                {days.map((d) => {
                  const todays = (eventsByRoom[r.id] || []).filter((e) => covers(e, d));
                  if (todays.length === 0) return <td key={d} className="px-1 py-2" />;
                  const top = todays[0];
                  return (
                    <td key={d} className="px-0.5 py-1.5" title={todays.map((t) => t.summary).join(" · ")}>
                      <div className={`h-5 w-full rounded ${dot(top)} ${todays.length > 1 ? "ring-1 ring-red-500 ring-inset" : ""}`} />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Legend({ color, label }) {
  return <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} /> {label}</span>;
}

// ---------- Reservations ----------
function ReservationsList() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState({});
  const [note, setNote] = useState({});
  const [moveId, setMoveId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [historyId, setHistoryId] = useState(null);
  const [cxlReqs, setCxlReqs] = useState([]);

  const load = async () => {
    try {
      const params = filter ? { status: filter } : {};
      const { data } = await api.get("/admin/booking/reservations", { params });
      setItems(data);
      try {
        const { data: cx } = await api.get("/admin/booking/cancellation-requests", { params: { status: "Pending" } });
        setCxlReqs(cx.requests || []);
      } catch { /* non-blocking */ }
    } catch (e) { toast.error(extractErr(e)); }
  };
  useEffect(() => { load(); }, [filter]);

  const transition = async (id, status) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await api.patch(`/admin/booking/reservations/${id}`, { status, admin_note: note[id] });
      toast.success(`Status → ${status}`);
      setNote((n) => ({ ...n, [id]: "" }));
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.conflicts) toast.error(`Conflict blocks confirmation (${detail.conflicts.length} overlap)`);
      else toast.error(extractErr(e));
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  const pendingCxlIds = new Set(cxlReqs.map((c) => c.reservation_id));
  const cxlCount = cxlReqs.reduce((m, c) => ({ ...m, [c.reservation_id]: (m[c.reservation_id] || 0) + 1 }), {});
  return (
    <div className="space-y-4" data-testid="reservations-list">
      {cxlReqs.length > 0 && (
        <div data-testid="cancellation-requests-banner" className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <span className="font-medium">{cxlReqs.length} cancellation request{cxlReqs.length > 1 ? "s" : ""} awaiting review.</span> Open a reservation's <span className="font-medium">History</span> to review and resolve — nothing is cancelled automatically.
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="res-filter" className={inp + " max-w-[220px]"}>
          <option value="">All statuses</option>
          {BOOKING_STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <button onClick={load} className={ghost + " text-xs"}>Refresh</button>
      </div>
      {items.length === 0 && <p className="text-sm text-[#78716C]">No reservations.</p>}
      <div className="space-y-3">
        {items.map((r) => (
          <div key={r.id} className={card} data-testid={`res-${r.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="text-[#1C1917] font-medium">{r.guest_name} <span className="font-light text-[#44403C]">· {r.guest_email}</span></p>
                <p className="text-xs text-[#78716C]">{r.reference ? <span className="font-mono text-[#1C1917]">{r.reference} · </span> : null}{r.room_name} · {r.check_in} → {r.check_out} · {r.num_guests} guest{r.num_guests > 1 ? "s" : ""} · {r.guest_phone || "no phone"}</p>
                {r.message && <p className="mt-2 text-sm text-[#44403C] font-light">{r.message}</p>}
                {r.has_conflict_on_create && (
                  <p className="mt-2 text-xs inline-flex items-center gap-1 text-amber-700"><AlertTriangle className="w-3 h-3" /> Overlapped existing calendar when submitted</p>
                )}
              </div>
              <div className="flex flex-col items-end gap-1.5">
                <StatusBadge status={r.status} />
                {r.payment_overdue && !["Declined", "Cancelled", "Confirmed"].includes(r.status) && (
                  <span data-testid={`res-overdue-${r.id}`} className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[11px] font-medium"><AlertTriangle className="w-3 h-3" /> Payment overdue</span>
                )}
                {pendingCxlIds.has(r.id) && (
                  <span data-testid={`res-cxl-badge-${r.id}`} className="inline-flex items-center gap-1 rounded-full bg-rose-100 text-rose-700 px-2 py-0.5 text-[11px] font-medium"><Ban className="w-3 h-3" /> Cancellation requested</span>
                )}
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-2">
              <input placeholder="Admin note (optional)" value={note[r.id] || ""} onChange={(e) => setNote({ ...note, [r.id]: e.target.value })} data-testid={`res-note-${r.id}`} className={inp + " md:col-span-2"} />
              <select onChange={(e) => e.target.value && transition(r.id, e.target.value)} value="" data-testid={`res-transition-${r.id}`} className={inp} disabled={busy[r.id]}>
                <option value="" disabled>Change status…</option>
                {BOOKING_STATUSES.filter((s) => s !== r.status).map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <button disabled={busy[r.id]} data-testid={`res-resend-invite-${r.id}`} className="text-xs underline underline-offset-4 text-[#1C1917] disabled:opacity-60"
                onClick={async () => { try { await api.post(`/admin/booking/reservations/${r.id}/resend-invite`); toast.success(`Guest Portal invite / reminder emailed to ${r.guest_email}`); load(); } catch (e) { toast.error(extractErr(e)); } }}>
                Resend Guest Portal invite & reminder
              </button>
              {r.last_reminder_at && <span className="text-xs text-[#78716C]">Last reminder {new Date(r.last_reminder_at).toLocaleString()}</span>}
              {!["Declined", "Cancelled"].includes(r.status) && (
                <button data-testid={`res-edit-${r.id}`} onClick={() => setEditId(r.id)}
                  className="text-xs inline-flex items-center gap-1 underline underline-offset-4 text-[#1C1917]">
                  <CalendarIcon className="w-3 h-3" /> Edit Reservation
                </button>
              )}
              {!["Declined", "Cancelled"].includes(r.status) && (
                <button data-testid={`res-move-${r.id}`} onClick={() => setMoveId(r.id)}
                  className="text-xs inline-flex items-center gap-1 underline underline-offset-4 text-[#1C1917]">
                  <ArrowRightLeft className="w-3 h-3" /> Move Reservation
                </button>
              )}
              <button data-testid={`res-history-${r.id}`} onClick={() => setHistoryId(r.id)}
                className="text-xs inline-flex items-center gap-1 underline underline-offset-4 text-[#1C1917]">
                <History className="w-3 h-3" /> History{cxlCount[r.id] ? ` · ${cxlCount[r.id]} request${cxlCount[r.id] > 1 ? "s" : ""}` : ""}
              </button>
            </div>
            {r.admin_note && <p className="mt-2 text-xs text-[#78716C] italic">Internal note: {r.admin_note}</p>}
          </div>
        ))}
      </div>
      {moveId && <MoveReservationModal reservation={items.find((x) => x.id === moveId)} onClose={() => setMoveId(null)} onDone={load} />}
      {editId && <EditReservationModal reservation={items.find((x) => x.id === editId)} onClose={() => setEditId(null)} onDone={load} />}
      {historyId && <StatusLogModal reservation={items.find((x) => x.id === historyId)} onClose={() => setHistoryId(null)} onResolved={load} />}
    </div>
  );
}

// ---------- Status history + cancellation requests (audit) ----------
const SOURCE_STYLE = {
  admin: "bg-stone-200 text-stone-800", guest: "bg-sky-100 text-sky-800",
  system: "bg-amber-100 text-amber-800", stripe: "bg-violet-100 text-violet-800",
  transfer: "bg-teal-100 text-teal-800", edit: "bg-stone-200 text-stone-800",
};
const EVENT_LABEL = {
  created: "Created", status_changed: "Status changed", payment_overdue_flagged: "Flagged payment overdue",
  payment_overdue_cleared: "Overdue cleared", cancellation_requested: "Guest requested cancellation",
  cancellation_request_resolved: "Cancellation request resolved", payment_link_created: "Payment link created",
  complimentary_transfer: "Complimentary room change", room_transferred: "Room transferred",
};
const PAY_STATE = {
  link_sent: { label: "Link sent · pending", cls: "bg-sky-100 text-sky-800" },
  paid: { label: "Paid", cls: "bg-emerald-100 text-emerald-800" },
  succeeded: { label: "Succeeded", cls: "bg-emerald-100 text-emerald-800" },
  pending: { label: "Pending", cls: "bg-stone-200 text-stone-700" },
  superseded: { label: "Superseded", cls: "bg-stone-200 text-stone-500" },
  expired: { label: "Expired", cls: "bg-amber-100 text-amber-800" },
  failed: { label: "Failed", cls: "bg-rose-100 text-rose-700" },
};
const fmtTs = (t) => (t ? new Date(t).toLocaleString() : null);

function StatusLogModal({ reservation, onClose, onResolved }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const load = async () => {
    try { const { data: d } = await api.get(`/admin/booking/reservations/${reservation.id}/status-log`); setData(d); }
    catch (e) { toast.error(extractErr(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const resolve = async (reqId) => {
    setBusy(reqId);
    try {
      await api.post(`/admin/booking/cancellation-requests/${reqId}/resolve`, { outcome: "reviewed" });
      toast.success("Cancellation request marked reviewed. To actually cancel, change the reservation's status.");
      await load(); onResolved();
    } catch (e) { toast.error(extractErr(e)); } finally { setBusy(""); }
  };
  const pending = (data?.cancellation_requests || []).filter((c) => c.status === "Pending");
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4" data-testid="status-log-modal">
      <div className="bg-white rounded-2xl max-w-xl w-full my-8 p-6 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A9A86]">Reservation history</p>
            <h3 className="text-lg font-medium text-[#1C1917]">{reservation.guest_name} · <span className="font-mono text-sm">{reservation.reference}</span></h3>
            <p className="text-xs text-[#78716C]">{reservation.room_name} · {reservation.check_in} → {reservation.check_out}</p>
          </div>
          <button onClick={onClose} data-testid="status-log-close" className={ghost + " text-xs"}>Close</button>
        </div>

        {pending.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="status-log-pending-requests">
            <p className="text-xs uppercase tracking-[0.15em] text-amber-700 font-medium">Pending cancellation request{pending.length > 1 ? "s" : ""}</p>
            {pending.map((c) => (
              <div key={c.id} className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="text-sm text-amber-900">Requested {new Date(c.requested_at).toLocaleString()}{c.reason ? ` · "${c.reason}"` : ""}</p>
                <p className="mt-1 text-[11px] text-amber-800">The reservation is unchanged. To cancel, set its status to Cancelled/Declined; otherwise mark the request reviewed.</p>
                <button disabled={busy === c.id} onClick={() => resolve(c.id)} data-testid={`cxl-resolve-${c.id}`} className={ghost + " text-xs mt-2"}>
                  {busy === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Mark reviewed
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="mt-5">
          <p className="text-xs uppercase tracking-[0.15em] text-[#8A9A86] font-medium mb-2">Audit trail</p>
          {!data ? <Loader2 className="w-4 h-4 animate-spin text-[#8A9A86]" /> : (data.entries.length === 0 ? (
            <p className="text-sm text-[#78716C]" data-testid="status-log-empty">No recorded events yet. Every future status change will be logged here with its source.</p>
          ) : (
            <ol className="space-y-2" data-testid="status-log-entries">
              {data.entries.slice().reverse().map((e) => (
                <li key={e.id} className="flex items-start gap-3 border-l-2 border-[#E7E5E4] pl-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${SOURCE_STYLE[e.source] || "bg-stone-200 text-stone-800"}`}>{e.source}</span>
                      <span className="text-sm text-[#1C1917] font-medium">{EVENT_LABEL[e.event] || e.event}</span>
                      {(e.from_status || e.to_status) && e.from_status !== e.to_status && (
                        <span className="text-xs text-[#78716C]">{e.from_status || "—"} → {e.to_status || "—"}</span>
                      )}
                    </div>
                    <p className="text-[11px] text-[#78716C]">{new Date(e.at).toLocaleString()} · {e.actor || "—"}{e.reason ? ` · ${e.reason}` : ""}</p>
                  </div>
                </li>
              ))}
            </ol>
          ))}
        </div>

        <div className="mt-6">
          <p className="text-xs uppercase tracking-[0.15em] text-[#8A9A86] font-medium mb-2">Payment activity</p>
          {!data ? null : ((data.payment_activity || []).length === 0 ? (
            <p className="text-sm text-[#78716C]" data-testid="payment-activity-empty">No payment links or charges yet.</p>
          ) : (
            <ol className="space-y-2" data-testid="payment-activity">
              {(data.payment_activity || []).slice().reverse().map((p) => {
                const st = PAY_STATE[p.status] || { label: p.status, cls: "bg-stone-200 text-stone-700" };
                const isRefund = p.kind === "refund";
                const title = isRefund
                  ? "Refund"
                  : `Installment ${p.installment ?? "—"}${p.part_count > 1 ? ` · part ${p.part}/${p.part_count}` : ""}`;
                return (
                  <li key={p.id} className="border-l-2 border-[#E7E5E4] pl-3" data-testid={`payment-activity-${p.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${isRefund ? "bg-rose-50 text-rose-700" : st.cls}`}>{isRefund ? "Refund" : st.label}</span>
                      <span className="text-sm text-[#1C1917] font-medium">{title}</span>
                      <span className="text-sm text-[#1C1917]">{isRefund ? "−" : ""}<Money v={Math.abs(Number(p.amount || 0))} /></span>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${SOURCE_STYLE[p.source] || "bg-stone-200 text-stone-800"}`}>{p.source}</span>
                    </div>
                    <div className="text-[11px] text-[#78716C] mt-0.5 space-y-0.5">
                      {p.created_at && <p>Created {fmtTs(p.created_at)}{p.created_by ? ` · by ${p.created_by}` : (!isRefund ? " · by admin" : "")}</p>}
                      {isRefund && p.requested_by && <p>Issued by {p.requested_by}</p>}
                      {p.sent_to_guest_at && <p>Sent to guest {fmtTs(p.sent_to_guest_at)}</p>}
                      {!isRefund && p.deadline && ["link_sent", "expired"].includes(p.status) && (
                        <p className={new Date(p.deadline) < new Date() ? "text-amber-700" : ""} data-testid={`payment-deadline-${p.id}`}>
                          {new Date(p.deadline) < new Date() ? "Link expired " : "Pay by "}{fmtTs(p.deadline)}
                        </p>
                      )}
                      {p.paid_at && <p>Paid {fmtTs(p.paid_at)}</p>}
                      {p.succeeded_at && <p>Refund succeeded {fmtTs(p.succeeded_at)}</p>}
                      {p.expired_at && <p>Expired {fmtTs(p.expired_at)}</p>}
                      {p.last_failure_at && <p className="text-rose-600">Last failure {fmtTs(p.last_failure_at)}{p.last_failure ? ` · ${p.last_failure}` : ""}{p.failed_attempts ? ` · ${p.failed_attempts} attempt(s)` : ""}</p>}
                      {(p.stripe_payment_intent || p.stripe_session || p.stripe_refund_id) && (
                        <p className="font-mono text-[#A8A29E]">{p.stripe_refund_id ? `refund …${p.stripe_refund_id}` : p.stripe_payment_intent ? `pi …${p.stripe_payment_intent}` : `cs …${p.stripe_session}`}</p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------- Move Reservation (room transfer) ----------
function Money({ v }) { return <>${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</>; }

function MoveReservationModal({ reservation, onClose, onDone }) {
  const [rooms, setRooms] = useState([]);
  const [dest, setDest] = useState("");
  const [tdate, setTdate] = useState("");
  const [quote, setQuote] = useState(null);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState("");
  const [mode, setMode] = useState("recalculate");

  const loadTransfers = async () => {
    try {
      const { data } = await api.get(`/admin/booking/reservations/${reservation.id}/transfers`);
      setPending(data.find((t) => ["pending", "needs_review", "expired"].includes(t.status)) || null);
    } catch (e) { /* noop */ }
  };
  useEffect(() => {
    api.get("/admin/booking/rooms").then(({ data }) => setRooms((data || []).filter((r) => r.active !== false && r.id !== reservation.room_id))).catch(() => {});
    loadTransfers();
  }, []); // eslint-disable-line

  const act = async (fn, label) => {
    setBusy(label);
    try { await fn(); } catch (e) { toast.error(extractErr(e)); } finally { setBusy(""); }
  };
  const preview = () => act(async () => {
    const { data } = await api.post(`/admin/booking/reservations/${reservation.id}/transfer/quote`, { to_room_id: dest, transfer_date: tdate || null, pricing_mode: mode });
    setQuote(data);
  }, "preview");
  const start = () => act(async () => {
    const { data } = await api.post(`/admin/booking/reservations/${reservation.id}/transfer`, { to_room_id: dest, transfer_date: tdate || null, pricing_mode: mode });
    toast.success(mode === "keep" ? "Complimentary transfer started — destination held" : "Transfer started — destination held");
    setPending(data); setQuote(null);
  }, "start");
  const makeLink = () => act(async () => {
    const { data } = await api.post(`/admin/booking/reservations/${reservation.id}/transfer/${pending.id}/upgrade-link`, {});
    toast.success("Upgrade payment link created for the guest");
    navigator.clipboard?.writeText(data.checkout_url).catch(() => {});
    loadTransfers();
  }, "link");
  const confirmMove = () => act(async () => {
    await api.post(`/admin/booking/reservations/${reservation.id}/transfer/${pending.id}/confirm`);
    toast.success("Reservation moved");
    onDone(); onClose();
  }, "confirm");
  const cancelMove = () => act(async () => {
    await api.post(`/admin/booking/reservations/${reservation.id}/transfer/${pending.id}/cancel`);
    toast.success("Transfer cancelled — original reservation retained");
    onDone(); setPending(null);
  }, "cancel");

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4" data-testid="move-reservation-modal">
      <div className="bg-white rounded-2xl max-w-xl w-full my-8 p-6 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A9A86]">Move reservation</p>
            <h3 className="text-lg font-medium text-[#1C1917]">{reservation.guest_name} · <span className="font-mono text-sm">{reservation.reference}</span></h3>
            <p className="text-xs text-[#78716C]">Currently in {reservation.room_name} · {reservation.check_in} → {reservation.check_out}</p>
          </div>
          <button onClick={onClose} data-testid="move-close" className={ghost + " text-xs"}>Close</button>
        </div>

        {!pending && (
          <div className="mt-5 space-y-3">
            <select value={dest} onChange={(e) => setDest(e.target.value)} data-testid="move-dest-room" className={inp}>
              <option value="">Select destination room…</option>
              {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            <div className="flex items-center gap-2 flex-wrap">
              <label className="text-xs text-[#78716C]">Transfer date (blank = whole stay / check-in)</label>
              <input type="date" value={tdate} onChange={(e) => setTdate(e.target.value)} data-testid="move-transfer-date" className={inp + " max-w-[200px]"} />
            </div>
            <div className="rounded-lg border border-[#E7E5E4] p-3 space-y-2" data-testid="move-pricing-mode">
              <p className="text-[11px] uppercase tracking-[0.15em] text-[#8A9A86] font-medium">Pricing</p>
              <label className="flex items-start gap-2 text-xs text-[#1C1917] cursor-pointer">
                <input type="radio" name="pricing-mode" value="recalculate" checked={mode === "recalculate"} onChange={() => { setMode("recalculate"); setQuote(null); }} data-testid="move-mode-recalculate" className="mt-0.5" />
                <span><span className="font-medium">Recalculate price / paid upgrade</span><br /><span className="text-[#78716C]">Use the destination room's pricing. Shows the upgrade difference; guest acceptance + payment required when applicable.</span></span>
              </label>
              <label className="flex items-start gap-2 text-xs text-[#1C1917] cursor-pointer">
                <input type="radio" name="pricing-mode" value="keep" checked={mode === "keep"} onChange={() => { setMode("keep"); setQuote(null); }} data-testid="move-mode-keep" className="mt-0.5" />
                <span><span className="font-medium">Keep original price / complimentary transfer</span><br /><span className="text-[#78716C]">Preserve the original total and payment schedule, waive the upgrade difference, no new payment link. Guest still accepts the addendum.</span></span>
              </label>
            </div>
            <p className="text-[11px] text-[#78716C]">{mode === "keep" ? "The guest must accept the transfer addendum; no upgrade payment is created. The original schedule (incl. any unpaid night) is preserved." : "The guest must accept the transfer addendum and pay any upgrade amount due before the move can be confirmed. Future monthly payments keep their scheduled due dates."}</p>
            <button disabled={!dest || busy} onClick={preview} data-testid="move-preview-btn" className={btn + " text-sm w-full justify-center"}>
              {busy === "preview" ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Preview transfer
            </button>
          </div>
        )}

        {quote && !pending && (
          <div className="mt-5 rounded-xl border border-[#E7E5E4] p-4 text-sm space-y-1.5" data-testid="move-quote">
            {!quote.available && <p className="text-red-600 text-xs">Not available: {(quote.unavailable_reasons || []).join(" ")}</p>}
            <Line k="From → To" v={`${quote.from_room.name} → ${quote.to_room.name}`} />
            <Line k="Type / date" v={`${quote.transfer_type === "full" ? "Full stay" : "Mid-stay"} · ${quote.transfer_date}`} />
            <Line k="Affected nights" v={`${quote.affected_nights} (${quote.prepaid_affected_nights} prepaid)`} />
            <Line k="Affected: source → dest" v={<><Money v={quote.affected_source.total} /> → <Money v={quote.affected_dest.total} /></>} />
            <Line k="Payments received" v={<Money v={quote.payments_received} />} />
            {quote.complimentary ? (
              <>
                <Line k="Upgrade difference" v={<span className="text-[#4A6A48]">Waived (<Money v={quote.waived_upgrade} />)</span>} strong />
                <Line k="Guest pays now" v={<Money v={0} />} strong />
              </>
            ) : (
              <Line k="Upgrade due now" v={<Money v={quote.upgrade_due_now} />} strong />
            )}
            {quote.credit_amount > 0 && <Line k="Proposed credit (review)" v={<Money v={quote.credit_amount} />} />}
            <Line k={quote.complimentary ? "Total (unchanged)" : "Revised total"} v={<Money v={quote.revised_grand_total} />} strong />
            <ul className="mt-2 pt-2 border-t border-[#E7E5E4] text-xs text-[#78716C] list-disc pl-4 space-y-1">
              {(quote.worked_example || []).map((w, i) => <li key={i}>{w}</li>)}
            </ul>
            <button disabled={!quote.available || busy} onClick={start} data-testid="move-start-btn" className={btn + " text-sm w-full justify-center mt-3"}>
              {busy === "start" ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Start transfer & hold destination
            </button>
          </div>
        )}

        {pending && (
          <div className="mt-5 rounded-xl border border-[#E7E5E4] p-4 text-sm space-y-2" data-testid="move-pending">
            <p className="text-xs text-[#78716C]">Status: <span className="font-medium text-[#1C1917]">{pending.status}</span> · Hold expires {new Date(pending.hold_expires_at).toLocaleString()}</p>
            <Line k="Destination" v={pending.to_room.name} />
            {pending.complimentary && <Line k="Pricing" v={<span className="text-[#4A6A48]">Complimentary (upgrade waived)</span>} strong />}
            <Line k="Upgrade due now" v={<Money v={pending.upgrade_due_now} />} strong />
            <Line k="Guest addendum" v={pending.addendum?.accepted_at ? `Accepted by ${pending.addendum.accepted_by}` : "Awaiting guest acceptance"} />
            <Line k="Upgrade paid" v={pending.upgrade_paid ? "Yes" : "No"} />
            {pending.upgrade_due_now > 0 && !pending.upgrade_checkout_url && (
              <button disabled={busy} onClick={makeLink} data-testid="move-create-link-btn" className={ghost + " text-xs"}>
                {busy === "link" ? <Loader2 className="w-3 h-3 animate-spin" /> : <LinkIcon className="w-3 h-3" />} Create guest upgrade payment link
              </button>
            )}
            <p className="text-[11px] text-[#78716C] bg-[#F5F5F4] rounded-lg p-2">The guest must accept the addendum and pay any upgrade in the Guest Portal before you can confirm. On confirm, the old room's transferred dates are released; Airbnb reflects this on its next iCal refresh of the room's calendar (allow a few hours, or adjust the Airbnb calendar manually if immediate).</p>
            <div className="flex gap-2 pt-1">
              <button disabled={busy} onClick={confirmMove} data-testid="move-confirm-btn" className={btn + " text-sm flex-1 justify-center"}>
                {busy === "confirm" ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Confirm move
              </button>
              <button disabled={busy} onClick={cancelMove} data-testid="move-cancel-btn" className={ghost + " text-sm"}>Cancel transfer</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Line({ k, v, strong }) {
  return <div className="flex justify-between gap-3"><span className="text-[#78716C]">{k}</span><span className={strong ? "font-semibold text-[#1C1917]" : "text-[#1C1917]"}>{v}</span></div>;
}

function EditReservationModal({ reservation, onClose, onDone }) {
  const [rooms, setRooms] = useState([]);
  const [roomId, setRoomId] = useState(reservation.room_id || "");
  const [ci, setCi] = useState(reservation.check_in || "");
  const [co, setCo] = useState(reservation.check_out || "");
  const [tdate, setTdate] = useState("");
  const [mode, setMode] = useState("keep");
  const [reason, setReason] = useState("");
  const [sum, setSum] = useState(null);
  const [busy, setBusy] = useState("");
  const roomChanged = roomId && roomId !== reservation.room_id;

  useEffect(() => {
    api.get("/admin/booking/rooms").then(({ data }) => setRooms(data || [])).catch(() => {});
  }, []);

  const body = () => ({ new_room_id: roomChanged ? roomId : null, check_in: ci, check_out: co,
    transfer_date: (roomChanged && tdate) ? tdate : null, pricing_mode: mode, reason });
  const act = async (fn, label) => { setBusy(label); try { await fn(); } catch (e) { toast.error(extractErr(e)); } finally { setBusy(""); } };
  const doPreview = () => act(async () => {
    const { data } = await api.post(`/admin/booking/reservations/${reservation.id}/edit/preview`, body());
    setSum(data);
  }, "preview");
  const doSave = () => act(async () => {
    await api.post(`/admin/booking/reservations/${reservation.id}/edit`, body());
    toast.success("Reservation updated");
    onDone(); onClose();
  }, "save");

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4" data-testid="edit-reservation-modal">
      <div className="bg-white rounded-2xl max-w-xl w-full my-8 p-6 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A9A86]">Edit reservation</p>
            <h3 className="text-lg font-medium text-[#1C1917]">{reservation.guest_name} · <span className="font-mono text-sm">{reservation.reference}</span></h3>
            <p className="text-xs text-[#78716C]">{reservation.room_name} · {reservation.check_in} → {reservation.check_out}</p>
          </div>
          <button onClick={onClose} data-testid="edit-close" className={ghost + " text-xs"}>Close</button>
        </div>

        <div className="mt-5 space-y-3">
          <div>
            <label className="text-xs text-[#78716C]">Room / property</label>
            <select value={roomId} onChange={(e) => setRoomId(e.target.value)} data-testid="edit-room" className={inp}>
              {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}{r.id === reservation.room_id ? " (current)" : ""}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-[#78716C]">Check-in</label><input type="date" value={ci} onChange={(e) => setCi(e.target.value)} data-testid="edit-checkin" className={inp} /></div>
            <div><label className="text-xs text-[#78716C]">Check-out</label><input type="date" value={co} onChange={(e) => setCo(e.target.value)} data-testid="edit-checkout" className={inp} /></div>
          </div>
          {roomChanged && (
            <div>
              <label className="text-xs text-[#78716C]">Transfer date (optional — for a checked-in guest; keeps completed nights on the current room)</label>
              <input type="date" value={tdate} onChange={(e) => setTdate(e.target.value)} data-testid="edit-transfer-date" className={inp} />
            </div>
          )}
          <div className="flex items-center gap-4 text-xs text-[#44403C]">
            <span className="text-[#78716C]">Price:</span>
            <label className="flex items-center gap-1"><input type="radio" name="pmode" checked={mode === "keep"} onChange={() => setMode("keep")} data-testid="edit-mode-keep" /> Keep agreed price</label>
            <label className="flex items-center gap-1"><input type="radio" name="pmode" checked={mode === "recalculate"} onChange={() => setMode("recalculate")} data-testid="edit-mode-recalc" /> Recalculate</label>
          </div>
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional, for booking history)" data-testid="edit-reason" className={inp} />
          <button disabled={busy} onClick={doPreview} data-testid="edit-preview-btn" className={btn + " text-sm w-full justify-center"}>
            {busy === "preview" ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Preview changes
          </button>
        </div>

        {sum && (
          <div className="mt-5 rounded-xl border border-[#E7E5E4] p-4 text-sm space-y-1.5" data-testid="edit-summary">
            {!sum.available && <p className="text-red-600 text-xs" data-testid="edit-unavailable">{sum.min_stay_message || "Not available — conflicts with existing bookings or blocked dates for the selected dates."}</p>}
            <Line k="Room" v={`${sum.from.room_name} → ${sum.to.room_name}`} />
            <Line k="Dates" v={`${sum.to.check_in} → ${sum.to.check_out}`} />
            {sum.mid_stay && <Line k="Transfer date" v={sum.to.transfer_date} />}
            <Line k="Current total" v={<Money v={sum.from.grand_total} />} />
            <Line k="Updated total" v={<Money v={sum.to.grand_total} />} strong />
            <Line k="Price difference" v={<Money v={sum.price_difference} />} />
            <Line k="Payments received" v={<Money v={sum.payments_received} />} />
            <Line k="New balance" v={<Money v={sum.new_balance} />} strong />
            <p className="text-[11px] text-[#78716C] pt-1">The guest is never charged or refunded automatically — any new balance is shown in their Guest Portal to pay via the usual links. Booking reference, ID documents and payment history are preserved.</p>
            <button disabled={!sum.available || busy} onClick={doSave} data-testid="edit-save-btn" className={btn + " text-sm w-full justify-center mt-2"}>
              {busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save changes
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    "Pending Review": "bg-[#FEF3C7] text-[#92400E]",
    "Availability Confirmed": "bg-[#E8EFE7] text-[#4A6A48]",
    "Awaiting Payment": "bg-[#DBEAFE] text-[#1E40AF]",
    "Confirmed": "bg-[#D1FAE5] text-[#065F46]",
    "Declined": "bg-[#FEE2E2] text-[#991B1B]",
    "Cancelled": "bg-[#F5F5F4] text-[#78716C] line-through",
  };
  return <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${map[status] || map["Pending Review"]}`}>{status}</span>;
}

// ---------- Inventory ----------
function InventoryList() {
  const [props, setProps] = useState([]);
  const [rooms, setRooms] = useState([]);
  const load = async () => {
    try {
      const [p, r] = await Promise.all([api.get("/admin/booking/properties"), api.get("/admin/booking/rooms")]);
      setProps(p.data); setRooms(r.data);
    } catch (e) { toast.error(extractErr(e)); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-5" data-testid="inventory-list">
      <div className={card}>
        <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#8A9A86]">Properties</p>
        <p className="mt-1 text-sm text-[#78716C]">{props.length} propert{props.length === 1 ? "y" : "ies"} in inventory.</p>
        <ul className="mt-3 space-y-1 text-sm text-[#1C1917]">
          {props.map((p) => <li key={p.id} data-testid={`prop-${p.slug}`}>• {p.name} <span className="text-[#78716C]">({p.type}, {p.city}) · {p.active ? "active" : "inactive"}</span></li>)}
        </ul>
      </div>
      <div className={card}>
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#8A9A86]">Rooms &amp; rates</p>
          <p className="text-xs text-[#78716C]">{rooms.length} room{rooms.length === 1 ? "" : "s"}</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-[#78716C] border-b border-[#E7E5E4]">
                <th className="py-2 pr-3">Name</th><th className="py-2 pr-3">Tier</th><th className="py-2 pr-3">Nightly</th>
                <th className="py-2 pr-3">Weekly</th><th className="py-2 pr-3">Monthly</th>
                <th className="py-2 pr-3">Min nights</th><th className="py-2 pr-3">Max guests</th><th className="py-2 pr-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {rooms.map((r) => (
                <tr key={r.id} className="border-b border-[#F5F5F4]" data-testid={`room-${r.slug}`}>
                  <td className="py-2 pr-3 text-[#1C1917]">{r.name}</td>
                  <td className="py-2 pr-3 text-[#44403C]">{r.tier}</td>
                  <td className="py-2 pr-3">${r.nightly_rate}</td>
                  <td className="py-2 pr-3">${r.weekly_rate}</td>
                  <td className="py-2 pr-3">${r.monthly_rate}</td>
                  <td className="py-2 pr-3">{r.min_nights}</td>
                  <td className="py-2 pr-3">{r.max_guests}</td>
                  <td className="py-2 pr-3">{r.active ? <span className="text-[#4A6A48]">active</span> : <span className="text-[#78716C]">inactive</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-[#78716C]">
          Add new rooms via <code className="bg-[#F5F5F4] rounded px-1">POST /api/portal/admin/booking/rooms</code>.
          Full admin create/edit UI is on the Phase 3 roadmap.
        </p>
      </div>
    </div>
  );
}

// ---------- Airbnb Sync ----------
function AirbnbSync() {
  const [rooms, setRooms] = useState([]);
  const load = async () => {
    try { const { data } = await api.get("/admin/booking/rooms"); setRooms(data); }
    catch (e) { toast.error(extractErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const copyExport = async (r) => {
    const url = `${PUBLIC_ICAL_BASE}/${r.id}.ics?token=${r.ical_export_token}`;
    try { await navigator.clipboard.writeText(url); toast.success("Export URL copied."); }
    catch { toast.error("Copy failed — long-press to copy."); }
  };
  const rotate = async (r) => {
    if (!window.confirm("Revoke the current export link? Airbnb will stop receiving Masaya blocks until you give it the new link.")) return;
    try { await api.post(`/admin/booking/rooms/${r.id}/ical/export-token`); toast.success("New export link issued."); load(); }
    catch (e) { toast.error(extractErr(e)); }
  };

  const isStale = (r) => Boolean(r.ical_sync_enabled) && (r.ical_last_status === "failed" || !r.ical_last_sync_at || Date.now() - new Date(r.ical_last_sync_at).getTime() > STALE_HOURS * 3600000);
  const staleCount = rooms.filter(isStale).length;

  return (
    <div className="space-y-4" data-testid="airbnb-sync">
      {staleCount > 0 && (
        <div data-testid="stale-calendar-warning" className="rounded-xl border border-[#FDE68A] bg-[#FEF3C7] p-4 text-sm text-[#92400E] flex gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          {staleCount} Airbnb calendar{staleCount === 1 ? " is" : "s are"} stale (not synced in {STALE_HOURS}h or last sync failed). Sync before approving direct requests for these rooms.
        </div>
      )}
      <div className={card}>
        <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#8A9A86]">Airbnb calendar synchronization</p>
        <p className="mt-2 text-sm text-[#44403C] font-light">
          For each room, paste the Airbnb calendar export link and press <strong>Verify</strong>. Masaya checks the link
          belongs to that room's Airbnb listing and previews the blocked dates without importing anything. Only after you
          <strong> Confirm & connect</strong> are Airbnb dates imported as read-only blocks. Airbnb stays the source of truth;
          nothing is ever changed on Airbnb. Use the <strong>Export URL</strong> to share Masaya's direct reservations with Airbnb.
        </p>
      </div>
      <div className="space-y-3">
        {rooms.map((r) => (
          <div key={r.id} className={card} data-testid={`sync-${r.slug}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="text-[#1C1917] font-medium">{r.name}{isStale(r) && <span data-testid={`stale-${r.slug}`} className="ml-2 text-[11px] rounded-full bg-[#FEF3C7] text-[#92400E] px-2 py-0.5">Stale</span>}</p>
                <p className="text-xs text-[#78716C]">
                  Airbnb listing: {r.airbnb_listing_id || "—"} · Last sync: {r.ical_last_sync_at?.replace("T", " ").slice(0, 16) || "never"} ·{" "}
                  <span className={r.ical_last_status === "ok" ? "text-[#4A6A48]" : r.ical_last_status === "failed" ? "text-red-600" : "text-[#78716C]"}>
                    {r.ical_last_status}
                  </span>
                  {r.ical_last_error && <span className="text-red-600" title={r.ical_last_error}> · error logged</span>}
                </p>
              </div>
            </div>
            <AirbnbConnect room={r} onChanged={load} />
            <div className="mt-3 flex items-center gap-2 text-xs text-[#78716C]">
              <LinkIcon className="w-3 h-3" />
              Masaya export URL (private):{" "}
              <code className="bg-[#F5F5F4] rounded px-1.5 py-0.5 text-[#1C1917]">{PUBLIC_ICAL_BASE}/{r.id}.ics?token=••••</code>
              <button onClick={() => copyExport(r)} className="inline-flex items-center gap-1 hover:text-[#1C1917]" data-testid={`sync-copy-${r.slug}`}>
                <Copy className="w-3 h-3" /> Copy
              </button>
              <button onClick={() => rotate(r)} className="inline-flex items-center gap-1 hover:text-[#1C1917]" data-testid={`sync-rotate-${r.slug}`}>Revoke & regenerate</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
